import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Check, Link2, Unlink, AlertCircle, Zap,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useLanguage } from '../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Localised strings for the Supabase (webhook mode) integration card.
const STRINGS = {
  de: {
    loading: 'Lovable-Website-Status wird geladen…',
    connected: 'Verbunden',
    notConnected: 'Nicht verbunden',
    disconnect: 'Trennen',
    fillAllFields: 'Bitte Webhook-URL und API-Key ausfüllen.',
    connectedToast: 'Lovable-Website verbunden.',
    connectionFailed: 'Verbindung fehlgeschlagen.',
    confirmDisconnect: 'Lovable-Website-Verbindung wirklich trennen? Bereits gesendete Beiträge sind davon nicht betroffen.',
    disconnectedToast: 'Lovable-Website-Verbindung getrennt.',
    disconnectFailed: 'Trennen fehlgeschlagen.',
    intro: 'Verbinde deine Website (Supabase Edge Function), um generierte Blog-Artikel direkt zu senden. Die Function schreibt den Beitrag selbst und aktualisiert ihn bei gleichem Slug.',
    webhookUrl: 'Webhook-URL',
    apiKey: 'API-Key',
    apiKeyHint: 'Wird verschlüsselt gespeichert und nie wieder angezeigt.',
    connect: 'Verbinden',
    checking: 'Verbindung wird gespeichert…',
  },
  en: {
    loading: 'Loading Lovable-Website status…',
    connected: 'Connected',
    notConnected: 'Not connected',
    disconnect: 'Disconnect',
    fillAllFields: 'Please fill in the webhook URL and API key.',
    connectedToast: 'Lovable-Website connected.',
    connectionFailed: 'Connection failed.',
    confirmDisconnect: 'Really disconnect the Lovable-Website? Already-sent posts are unaffected.',
    disconnectedToast: 'Lovable-Website disconnected.',
    disconnectFailed: 'Disconnect failed.',
    intro: 'Connect your website (Supabase Edge Function) to send generated blog articles directly. The function writes the post itself and updates it on matching slug.',
    webhookUrl: 'Webhook URL',
    apiKey: 'API key',
    apiKeyHint: 'Stored encrypted and never shown again.',
    connect: 'Connect',
    checking: 'Saving connection…',
  },
};

/**
 * SupabaseCard — connect/disconnect a project's Supabase Edge-Function webhook.
 * Webhook mode only. Mirrors WordPressCard's layout and styling. Lives inside
 * Settings.js integrations tab, next to WordPressCard.
 */
export default function SupabaseCard({ getAuthHeader, embedded = false, onStatusChange }) {
  const { lang } = useLanguage();
  const t = STRINGS[lang === 'en' ? 'en' : 'de'];
  const [status, setStatus] = useState({ loading: true, connected: false, connection: null });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  // Keep onStatusChange in a ref so `load` does NOT depend on it. Parents often
  // pass an inline callback (new identity each render); if it were a `load`
  // dependency, calling it here would re-create load → re-fire the effect →
  // reload → call it again → infinite loop (the flicker regression).
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);
  // Last successfully loaded connected value (null = never loaded). On a failed
  // reload we keep this instead of downgrading a known-good status to "false".
  const lastConnectedRef = useRef(null);

  const load = useCallback(async () => {
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const { data } = await axios.get(`${API}/integrations/supabase/status`, { headers: getAuthHeader(), timeout: 8000 });
      const connected = !!data.connected;
      lastConnectedRef.current = connected;
      setStatus({ loading: false, connected, connection: data.connection || null });
      onStatusChangeRef.current?.(connected);
    } catch (_e) {
      // Error/timeout: never overwrite a known-good status. Keep the last loaded
      // state (only stop the spinner) and report the preserved value.
      setStatus((s) => ({ ...s, loading: false }));
      onStatusChangeRef.current?.(lastConnectedRef.current ?? false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    setConnectError('');
    if (!webhookUrl.trim() || !apiKey.trim()) {
      setConnectError(t.fillAllFields);
      return;
    }
    setConnecting(true);
    try {
      await axios.post(
        `${API}/integrations/supabase/connect`,
        { mode: 'webhook', webhook_url: webhookUrl.trim(), webhook_api_key: apiKey.trim() },
        { headers: getAuthHeader(), timeout: 45000 }
      );
      toast.success(t.connectedToast);
      setWebhookUrl(''); setApiKey('');
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || t.connectionFailed;
      setConnectError(msg);
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t.confirmDisconnect)) return;
    setDisconnecting(true);
    try {
      await axios.post(`${API}/integrations/supabase/disconnect`, {}, { headers: getAuthHeader() });
      toast.success(t.disconnectedToast);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.disconnectFailed);
    } finally {
      setDisconnecting(false);
    }
  };

  if (status.loading) {
    return (
      <div className="bg-[#1A2B42] rounded-lg p-5 flex items-center gap-3" data-testid="supabase-card-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#00D9C8]" />
        <span className="text-sm text-[#A0AEC0]">{t.loading}</span>
      </div>
    );
  }

  const c = status.connection;
  const host = (c?.webhook_url || '').replace(/^https?:\/\//, '');

  return (
    <div className={embedded ? '' : 'bg-[#1A2B42] rounded-lg p-5'} data-testid="supabase-integration-card">
      <div className={`flex items-center justify-between ${embedded ? 'mb-3' : 'mb-4'}`}>
        <div className="flex items-center gap-3">
          {!embedded && (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#3ECF8E]">
              <Zap className="w-6 h-6 text-[#0A1628]" fill="currentColor" aria-hidden="true" />
            </div>
          )}
          <div>
            {!embedded && <h3 className="text-white font-medium">Lovable-Website</h3>}
            {status.connected ? (
              <p className="text-xs flex items-center gap-1 text-emerald-400" data-testid="supabase-connection-status">
                <Check className="w-3 h-3" /> {t.connected}
                {host && <span className="font-medium break-all">· {host}</span>}
              </p>
            ) : (
              <p className="text-xs text-[#5A6C7D]" data-testid="supabase-connection-status">{t.notConnected}</p>
            )}
          </div>
        </div>
        {status.connected && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleDisconnect}
            disabled={disconnecting}
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            data-testid="supabase-disconnect-btn"
          >
            {disconnecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Unlink className="w-3 h-3 mr-1" />}
            {t.disconnect}
          </Button>
        )}
      </div>

      {!status.connected && (
        <div className="space-y-3">
          <p className="text-xs text-[#A0AEC0] leading-relaxed">{t.intro}</p>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs text-[#A0AEC0]">{t.webhookUrl}</Label>
              <Input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://xxxx.supabase.co/functions/v1/posts-webhook"
                className="bg-[#0A1628] border-transparent text-white text-sm mt-1"
                data-testid="supabase-webhook-url-input"
              />
            </div>
            <div>
              <Label className="text-xs text-[#A0AEC0]">{t.apiKey}</Label>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="••••••••••••••••"
                className="bg-[#0A1628] border-transparent text-white text-sm mt-1"
                data-testid="supabase-api-key-input"
              />
              <p className="text-[10px] text-[#5A6C7D] mt-1">{t.apiKeyHint}</p>
            </div>
          </div>

          {connectError && (
            <div className="flex items-start gap-2 text-xs bg-rose-500/10 border border-rose-500/30 text-rose-300 rounded px-3 py-2" data-testid="supabase-connect-error">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{connectError}</span>
            </div>
          )}

          <Button
            onClick={handleConnect}
            disabled={connecting}
            className="bg-[#3ECF8E] hover:bg-[#34b87c] text-[#0A1628] font-medium"
            data-testid="supabase-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
            {connecting ? t.checking : t.connect}
          </Button>
        </div>
      )}
    </div>
  );
}
